import type { MapDef } from '../world/map';
import { T } from '../world/tiles';
import { fbm, hash2, hex, mix, noise2, scale, smoothstep, type RGB } from './noise';

/**
 * Ground materials of the barcelona package. Tile chars reserved for it: '<', '>' walkable, '|' solid
 * (solidity is declared in src/world/tiles.ts). Runs inside the ground worker, so keep it pure: no DOM,
 * no game state. Return null for chars you don't own.
 *
 *  '<'  Barcelona's panot pavement: 20 cm cement tiles, some with the four-petal flower. Inside AIRPORT
 *       it is the terminal's polished grey slabs; inside STAIRS the ochre steps of Park Güell's stairway.
 *  '>'  old cobbles (the Barri Gòtic). Inside RAMBLA the wavy three-tone paving of the promenade,
 *       inside BEACH the sand of the Barceloneta (wet near the water), inside GUELL ochre gravel.
 *  '|'  building blocks seen from above: terracotta roofs (slate in the Gòtic, steel at the airport).
 */
type R4 = readonly [number, number, number, number];
const AIRPORT: R4 = [0, 38, 16, 12];
const TERMINAL: R4 = [0, 50, 16, 6];
const STAIRS: R4 = [14, 11, 4, 4];
const RAMBLA: R4 = [28, 29, 6, 13];
const BEACH: R4 = [42, 46, 54, 5];
const GUELL: R4 = [0, 0, 32, 21];
const GOTIC: R4 = [38, 26, 35, 18];
const WHOTEL: R4 = [90, 44, 6, 6];

const inRect = (tx: number, ty: number, r: R4) => tx >= r[0] && ty >= r[1] && tx < r[0] + r[2] && ty < r[1] + r[3];
const at = (map: MapDef, tx: number, ty: number) => (tx < 0 || ty < 0 || tx >= map.w || ty >= map.h ? ' ' : map.rows[ty][tx]);

const K = {
  panotA: hex('#c9c4b8'),
  panotB: hex('#b4aea2'),
  panotJoint: hex('#8c877c'),
  petal: hex('#a9a498'),
  slabA: hex('#c3c6c9'),
  slabB: hex('#aeb2b6'),
  slabJoint: hex('#7d8186'),
  stepA: hex('#d8b47a'),
  stepB: hex('#b9925a'),
  cobbleA: hex('#8b8784'),
  cobbleB: hex('#6d6a68'),
  cobbleJoint: hex('#3f3d3b'),
  ramblaA: hex('#e7e0cf'),
  ramblaB: hex('#a39d92'),
  ramblaC: hex('#4c4a48'),
  sandA: hex('#ead9ab'),
  sandB: hex('#d4bd8a'),
  sandWet: hex('#b49a6c'),
  gravelA: hex('#cfa96c'),
  gravelB: hex('#b48d55'),
  tileA: hex('#c7784a'),
  tileB: hex('#9c5a36'),
  slateA: hex('#6c6a70'),
  slateB: hex('#4c4a52'),
  steel: hex('#9aa4ac'),
  glass: hex('#6a9ac0'),
};

/** Panot: 8 px tiles with a 1 px joint; every third tile or so carries the flower (four petals round a dot). */
function panot(x: number, y: number): RGB {
  const s = 8;
  const cx = Math.floor(x / s), cy = Math.floor(y / s);
  const fx = x - cx * s, fy = y - cy * s;
  const tone = hash2(cx, cy, 501);
  let c = mix(K.panotA, K.panotB, tone * 0.6 + fbm(x / 24, y / 24, 502) * 0.4);
  if (fx < 0.8 || fy < 0.8) return mix(c, K.panotJoint, 0.7);
  if (tone < 0.42) {
    // The flower: four petals at the cardinal points, a dot in the middle (relief shows as a darker tone).
    const dx = fx - 4.4, dy = fy - 4.4;
    const d = Math.hypot(dx, dy);
    const petal = Math.min(Math.hypot(Math.abs(dx) - 2.2, dy), Math.hypot(dx, Math.abs(dy) - 2.2));
    if (petal < 1.1 || d < 0.9) c = mix(c, K.petal, 0.75);
    else if (petal < 1.6) c = scale(c, 1.05);
  } else {
    // Four squares with a cross-shaped groove.
    if (Math.abs(fx - 4.4) < 0.5 || Math.abs(fy - 4.4) < 0.5) c = mix(c, K.panotJoint, 0.45);
  }
  // Wear: a few chipped or stained tiles.
  if (hash2(cx, cy, 503) > 0.94) c = scale(c, 0.9);
  if (hash2(Math.floor(x * 2), Math.floor(y * 2), 504) < 0.03) c = scale(c, 0.93);
  return c;
}

/** The terminal: big polished slabs with a sheen that streaks along the hall. */
function terminalFloor(x: number, y: number): RGB {
  const s = 16;
  const cx = Math.floor(x / s), cy = Math.floor(y / s);
  const fx = x - cx * s, fy = y - cy * s;
  let c = mix(K.slabA, K.slabB, hash2(cx, cy, 511) * 0.5 + noise2(x / 10, y / 10, 512) * 0.5);
  if (fx < 0.9 || fy < 0.9) return mix(c, K.slabJoint, 0.55);
  const sheen = fbm(x / 30 + y / 90, y / 9, 513);
  if (sheen > 0.62) c = mix(c, hex('#eef2f5'), (sheen - 0.62) * 1.4);
  return c;
}

/** Park Güell's stairway: ochre stone treads with a lit edge and a darker riser every 4 px. */
function steps(x: number, y: number): RGB {
  const fy = ((y % 4) + 4) % 4;
  let c = mix(K.stepA, K.stepB, noise2(x * 0.3, y * 0.2, 521));
  if (fy < 0.8) c = scale(c, 1.1);
  else if (fy > 2.9) c = scale(c, 0.68);
  // Trencadís flecks along the balustrade side of each tread.
  const fx = ((x % 16) + 16) % 16;
  if ((fx < 1.5 || fx > 14.5) && hash2(Math.floor(x), Math.floor(y / 2), 522) > 0.5) return [hex('#3a8fd9'), hex('#e2493b'), hex('#ffd34e'), hex('#f4f1ea')][Math.floor(hash2(Math.floor(x), Math.floor(y / 2), 523) * 4)];
  return c;
}

/** Old cobbles: fist-sized stones in rough rows, dark joints, a little moss and damp. */
function cobbles(x: number, y: number): RGB {
  const s = 5.2;
  const gy = Math.floor(y / s), off = (gy % 2) * s * 0.5;
  const gx = Math.floor((x + off) / s);
  const pcx = (gx + 0.5) * s - off + (hash2(gx, gy, 531) - 0.5) * 1.4, pcy = (gy + 0.5) * s + (hash2(gx, gy, 532) - 0.5) * 1.2;
  const rx = s * (0.44 + hash2(gx, gy, 533) * 0.1), ry = rx * 0.82;
  const d = Math.hypot((x - pcx) / rx, (y - pcy) / ry);
  let c = mix(K.cobbleA, K.cobbleB, hash2(gx, gy, 534));
  if (d > 1) return mix(K.cobbleJoint, c, 0.25 + (hash2(gx, gy, 535) < 0.08 ? 0.3 : 0));
  if (d < 0.5 && y < pcy && x < pcx) c = mix(c, hex('#b8b4b0'), 0.25);
  else if (d > 0.8) c = scale(c, 0.85);
  const damp = fbm(x / 36, y / 36, 536);
  if (damp > 0.6) c = mix(c, hex('#4a5a50'), (damp - 0.6) * 0.8);
  return c;
}

/** La Rambla: the famous wavy pavement in cream, grey and charcoal bands. */
function ramblaWaves(x: number, y: number): RGB {
  const wave = x + Math.sin(y / 10) * 7 + Math.sin(y / 23 + 1) * 3;
  const band = Math.floor(wave / 5);
  const k = ((band % 4) + 4) % 4;
  let c = k === 0 ? K.ramblaA : k === 1 ? K.ramblaB : k === 2 ? K.ramblaA : K.ramblaC;
  // Stone joints every 5 px along the band and every 6 px across it.
  const fb = wave - band * 5;
  if (fb < 0.6 || ((y % 6) + 6) % 6 < 0.6) c = mix(c, K.cobbleJoint, 0.35);
  c = scale(c, 0.94 + noise2(x * 0.5, y * 0.5, 541) * 0.12);
  if (hash2(Math.floor(x * 2), Math.floor(y * 2), 542) < 0.02) c = scale(c, 0.9);
  return c;
}

/** Barceloneta sand: fine grain, footprints, a darker wet band where the waves reach. */
function sand(map: MapDef, x: number, y: number): RGB {
  const tx = Math.floor(x / T), ty = Math.floor(y / T);
  const fy = y - ty * T;
  let c = mix(K.sandA, K.sandB, fbm(x / 18, y / 18, 551) * 0.7 + hash2(Math.floor(x), Math.floor(y), 552) * 0.3);
  // Footprints: little paired dents walking about.
  const fp = hash2(Math.floor(x / 7), Math.floor(y / 5), 553);
  if (fp > 0.86 && ((x % 7) + 7) % 7 < 2.4 && ((y % 5) + 5) % 5 < 1.6) c = scale(c, 0.9);
  // Shells and pebbles.
  if (hash2(Math.floor(x * 1.5), Math.floor(y * 1.5), 554) > 0.985) c = hash2(Math.floor(x), Math.floor(y), 555) > 0.5 ? hex('#f8f2e6') : hex('#9a8a70');
  // Wet where the next tile south is sea; a ripple of foam at the very edge.
  if (at(map, tx, ty + 1) === 'Z') {
    const wet = smoothstep(3, T, fy + (noise2(x / 9, 0, 556) - 0.5) * 6);
    c = mix(c, K.sandWet, wet * 0.8);
    if (fy > T - 1.6 && noise2(x / 5, 0, 557) > 0.4) c = mix(c, hex('#f6f2ea'), 0.6);
  }
  return c;
}

/** Park Güell paths: ochre gravel, grass creeping in at the edges, a stone every so often. */
function gravel(map: MapDef, x: number, y: number): RGB {
  const tx = Math.floor(x / T), ty = Math.floor(y / T);
  const fx = x - tx * T, fy = y - ty * T;
  let c = mix(K.gravelA, K.gravelB, hash2(Math.floor(x * 1.3), Math.floor(y * 1.3), 561) * 0.5 + fbm(x / 20, y / 20, 562) * 0.5);
  if (hash2(Math.floor(x), Math.floor(y), 563) > 0.96) c = scale(c, 0.8);
  let edge = 0;
  if (at(map, tx - 1, ty) === 'g') edge = Math.max(edge, smoothstep(4, 0, fx));
  if (at(map, tx + 1, ty) === 'g') edge = Math.max(edge, smoothstep(T - 4, T, fx));
  if (at(map, tx, ty - 1) === 'g') edge = Math.max(edge, smoothstep(4, 0, fy));
  if (at(map, tx, ty + 1) === 'g') edge = Math.max(edge, smoothstep(T - 4, T, fy));
  if (edge > 0 && noise2(x / 3, y / 3, 564) > 0.45) c = mix(c, hex('#6f9a3a'), edge * 0.7);
  return c;
}

/** Roofs seen from above: rows of curved terracotta tiles (slate in the Gòtic, steel sheets on the terminal). */
function roof(tx: number, ty: number, x: number, y: number): RGB {
  if (inRect(tx, ty, TERMINAL)) {
    let c = mix(K.steel, scale(K.steel, 0.85), ((x % 12) + 12) % 12 < 1 ? 1 : 0);
    c = scale(c, 0.95 + noise2(x / 8, y / 8, 571) * 0.1);
    return c;
  }
  if (inRect(tx, ty, WHOTEL)) {
    const c = mix(K.glass, hex('#a8cce0'), noise2(x / 6, y / 6, 572));
    return ((y % 6) + 6) % 6 < 1 ? scale(c, 0.7) : c;
  }
  const gothic = inRect(tx, ty, GOTIC);
  const a = gothic ? K.slateA : K.tileA, b = gothic ? K.slateB : K.tileB;
  const row = Math.floor(y / 3);
  const fy = y - row * 3;
  const off = (row % 2) * 2;
  const col = Math.floor((x + off) / 4);
  let c = mix(a, b, hash2(col, row, 573) * 0.5 + fbm(x / 30, y / 30, 574) * 0.5);
  if (fy < 0.8) c = scale(c, 0.7);
  else if (fy < 1.6) c = scale(c, 1.08);
  const fx = x + off - col * 4;
  if (fx < 0.6) c = scale(c, 0.85);
  return c;
}

export function colorAtBarcelona(map: MapDef, x: number, y: number, mat: string): RGB | null {
  if (mat !== '<' && mat !== '>' && mat !== '|') return null;
  const tx = Math.floor(x / T), ty = Math.floor(y / T);
  const here = map.id === 'barcelona';
  switch (mat) {
    case '<':
      if (here && inRect(tx, ty, STAIRS)) return steps(x, y);
      if (here && inRect(tx, ty, AIRPORT)) return terminalFloor(x, y);
      return panot(x, y);
    case '>':
      if (here && inRect(tx, ty, RAMBLA)) return ramblaWaves(x, y);
      if (here && inRect(tx, ty, BEACH)) return sand(map, x, y);
      if (here && inRect(tx, ty, GUELL)) return gravel(map, x, y);
      return cobbles(x, y);
    default:
      return roof(tx, ty, x, y);
  }
}

/** Flat colours shown while the detailed ground is still rendering. */
export const PLACEHOLDER_BARCELONA: Record<string, string> = { '<': '#c3beb2', '>': '#7d7a77', '|': '#b06a42' };
